import type { PanelBlock } from './types';
import { log } from './logger';

const KEY = 'agent-sdk-state-v1';
const MAX_BLOCKS = 60;

export type ApprovalOutcome = 'approved' | 'declined';

export interface PersistedState {
  user: string;
  sessionId: string;
  open: boolean;
  width: number;
  blocks: PanelBlock[];
  approvals: Record<string, ApprovalOutcome>;
  dismissed: boolean;
  selfNavigated: boolean;
}

/**
 * Storage is per tab, not per person, so signing out and signing back in as
 * somebody else would otherwise hand the new user the previous one's session id
 * and transcript. Anything not written by the user now signing in is theirs to
 * lose, including state from a build that recorded no user at all — if we cannot
 * say whose it is, we do not keep it.
 */
export function bindUser(id: string): void {
  const stored = load();
  if (stored.user === id) return;

  if (stored.sessionId !== undefined || stored.blocks !== undefined) {
    log.info('discarded panel state belonging to a different user');
  }
  clear();
  save({ user: id });
  flush();
}

export function load(): Partial<PersistedState> {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed as Partial<PersistedState>;
  } catch {
    return {};
  }
}

let current: Partial<PersistedState> | null = null;
let dirty = false;
let timer: ReturnType<typeof setTimeout> | undefined;

export function save(patch: Partial<PersistedState>): void {
  current = { ...(current ?? load()), ...patch };
  dirty = true;
  if (timer) return;
  timer = setTimeout(flush, 250);
}

export function flush(): void {
  if (timer) {
    clearTimeout(timer);
    timer = undefined;
  }
  if (!dirty || !current) return;
  dirty = false;
  const state = { ...current };
  if (Array.isArray(state.blocks) && state.blocks.length > MAX_BLOCKS) {
    state.blocks = state.blocks.slice(-MAX_BLOCKS);
  }
  current = state;
  try {
    sessionStorage.setItem(KEY, JSON.stringify(state));
  } catch (error) {
    log.warn('could not persist panel state', error);
  }
}

export function markSelfNavigation(): void {
  save({ selfNavigated: true });
  flush();
}

export function consumeSelfNavigation(): boolean {
  const was = load().selfNavigated === true;
  if (was) {
    save({ selfNavigated: false });
    flush();
  }
  return was;
}

export function clear(): void {
  current = null;
  dirty = false;
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* storage unavailable */
  }
}
